import Microphone from '@components/icons/FileMicrophone.svg';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { Content } from '@modals/UnlockJobModal/styles/Content';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { EErrorMessages } from 'app/types/errorMessages';
import { formatCentsPrice } from '@helpers/formatCentsPrice';
import { formatFileSize } from '@helpers/formatFileSize';
import { handleDownloadError } from '@helpers/jobLinkHelpers';
import { downloadViaBrowser } from '@helpers/downloadViaBrowser';
import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PayService } from 'app/API';
import { ApiCallError } from 'app/http/ApiCallError';
import { getApiCallErrorMessage } from 'app/http/getApiCallErrorMessage';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { createFetchJobUnlockPricingQueryFn } from 'app/queries/jobs/createFetchJobUnlockPricingQueryFn';
import { createFetchPartialUnlockedFilesQueryFn } from 'app/queries/jobs/createFetchPartialUnlockedFilesQueryFn';
import { jobsKeys } from 'app/queries/jobs/jobsKeys';
import { FileDownloadButton } from '@components/FileDownloadButton';
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import ChoiceOptionIcon from '@components/icons/ChoiceOption.svg';
import DownloadFileIcon from '@components/icons/DownloadFile.svg';
import SummaryIcon from '@components/icons/FileDocument.svg';
import { FileSection } from './styles/FileSection';
import { UnlockChoiceOption } from './styles/UnlockChoiceOption';
import { TProps } from './types/TProps';

const PARTIAL_UNLOCK_POLL_INTERVAL_MS = 5000;

export function UnlockJobModal({ id, name, onSuccess, onCancel }: TProps) {
  const { closeModal, openModal } = useModalContext();
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const loadingToastIdRef = useRef<string>();

  const priceQuery = useQuery({
    queryKey: jobsKeys.unlockPricing(id),
    queryFn: createFetchJobUnlockPricingQueryFn(call),
  });

  const partialChargeMutation = useMutation({
    mutationFn: () => call(() => PayService.partialCharge({ jobId: id })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobsKeys.unlockPricing(id) });
    },
    onError: notifyApiCallError,
  });

  const partialUnlockProcessed = partialChargeMutation.isSuccess || !!priceQuery.data?.result?.partialUnlocked;

  const filesQuery = useQuery({
    queryKey: jobsKeys.partialUnlockedFilesByJob(id),
    queryFn: createFetchPartialUnlockedFilesQueryFn(call),
    enabled: partialUnlockProcessed,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (query.state.status === 'error' || data?.error || (data?.downloadOutputUrl && data?.downloadSummaryUrl)) {
        return false;
      }
      return PARTIAL_UNLOCK_POLL_INTERVAL_MS;
    },
  });

  const hasFreshFiles = filesQuery.isFetchedAfterMount;
  const files = hasFreshFiles ? filesQuery.data : undefined;
  const error = hasFreshFiles && (filesQuery.isError || !!files?.error);
  const filesReady = !error && !!files?.downloadOutputUrl && !!files?.downloadSummaryUrl;
  const isWaitingForSummary = partialUnlockProcessed && !!files && !error && !filesReady;
  const isLoading = partialChargeMutation.isPending || (partialUnlockProcessed && !error && !filesReady);

  const unlockPrice = Number(priceQuery.data?.result?.amount ?? 0);
  const partialUnlockRate = Number(priceQuery.data?.result?.partialUnlockRate ?? 0);
  const outputUrl = filesReady ? files?.downloadOutputUrl ?? '' : '';
  const summaryUrl = filesReady ? files?.downloadSummaryUrl ?? '' : '';
  const reporterNotesTxtUrl = filesReady ? files?.downloadReporterNotesTxt : '';
  const reporterNotesRtfUrl = filesReady ? files?.downloadReporterNotesRtf : '';
  const outputFileSize = files?.downloadOutputFileSize || 0;
  const summaryFileSize = files?.downloadSummaryFileSize || 0;
  const reporterNotesTxtFileSize = files?.downloadReporterNotesTxtFileSize || 0;
  const reporterNotesRtfFileSize = files?.downloadReporterNotesRtfFileSize || 0;
  const sourceAudioFiles = (filesReady ? files?.sourceAudioFiles : undefined) ?? [];

  useEffect(() => {
    if (!priceQuery.error) {
      return;
    }
    toast.error(
      priceQuery.error instanceof ApiCallError ? getApiCallErrorMessage(priceQuery.error) : EErrorMessages.DEFAULT,
    );
    onCancel();
  }, [priceQuery.error, onCancel]);

  useEffect(() => {
    if (isWaitingForSummary && !loadingToastIdRef.current) {
      loadingToastIdRef.current = toast.loading('Waiting for the summary generation...');
    }
  }, [isWaitingForSummary]);

  useEffect(() => {
    if (!filesReady || !loadingToastIdRef.current) {
      return;
    }
    toast.remove(loadingToastIdRef.current);
    loadingToastIdRef.current = undefined;
    toast.success('Reporter unlock has been successfully processed');
  }, [filesReady]);

  useEffect(() => {
    if (!error) {
      return;
    }
    toast.error(EErrorMessages.DEFAULT);
    if (loadingToastIdRef.current) {
      toast.remove(loadingToastIdRef.current);
      loadingToastIdRef.current = undefined;
    }
  }, [error]);

  useEffect(
    () => () => {
      if (loadingToastIdRef.current) {
        toast.remove(loadingToastIdRef.current);
      }
    },
    [],
  );

  return (
    <ModalWrapper width="460px">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Unlock The Job</ModalTitle>

      <Content>
        <UnlockChoiceOption
          onClick={() => {
            closeModal();
            openModal(ModalNamesEnum.UnlockJobFull, { id, name, onSuccess, onCancel });
          }}
        >
          <div>
            <div className="title">Full unlock</div>
            <div className="description">Get full access to the job</div>
            <div className="price">{formatCentsPrice(unlockPrice || 0)}</div>
          </div>
          <ChoiceOptionIcon />
        </UnlockChoiceOption>

        {partialUnlockProcessed ? (
          <div className="mt-2">
            {error ? (
              <div className="text-red-500 text-sm mt-2 w-max[100px]">
                <div>Something went wrong while retrieving the summary.</div>
                <div>Please contact support.</div>
              </div>
            ) : (
              <>
                <FileSection>
                  <SummaryIcon className="icon" />
                  <div className="file-description">
                    <div>Court_Reporting_Summary.PDF</div>
                    <div>File size {formatFileSize(summaryFileSize)}</div>
                  </div>
                  <a href={summaryUrl} target="_blank" rel="noreferrer">
                    <DownloadFileIcon />
                  </a>
                </FileSection>

                <FileSection className="mt-2">
                  <Microphone className="icon" />
                  <div className="file-description">
                    <div>Court_Reporting_Audio.MP3</div>
                    <div>File size {formatFileSize(outputFileSize)}</div>
                  </div>
                  <a href={outputUrl} target="_blank" rel="noreferrer">
                    <DownloadFileIcon />
                  </a>
                </FileSection>

                {!!reporterNotesTxtUrl && (
                  <FileSection className="mt-2">
                    <SummaryIcon className="icon" />
                    <div className="file-description">
                      <div>Reporter Notes TXT file</div>
                      <div>File size {formatFileSize(reporterNotesTxtFileSize)}</div>
                    </div>
                    <a href={reporterNotesTxtUrl} target="_blank" rel="noreferrer">
                      <DownloadFileIcon />
                    </a>
                  </FileSection>
                )}

                {!!reporterNotesRtfUrl && (
                  <FileSection className="mt-2">
                    <SummaryIcon className="icon" />
                    <div className="file-description">
                      <div>Reporter Notes RTF file</div>
                      <div>File size {formatFileSize(reporterNotesRtfFileSize)}</div>
                    </div>
                    <a href={reporterNotesRtfUrl} target="_blank" rel="noreferrer">
                      <DownloadFileIcon />
                    </a>
                  </FileSection>
                )}

                {sourceAudioFiles.length > 0 ? (
                  <>
                    <div className="mt-3 mb-1 text-xs font-semibold text-[var(--color-text-muted)]">Source Audio</div>
                    {sourceAudioFiles.map((sourceFile) => (
                      <FileSection key={sourceFile.fileName} className="mt-2">
                        <Microphone className="icon" />
                        <div className="file-description">
                          <div>{sourceFile.fileName}</div>
                          {sourceFile.fileSize ? <div>File size {formatFileSize(sourceFile.fileSize)}</div> : null}
                        </div>
                        <a href={sourceFile.downloadUrl} target="_blank" rel="noreferrer">
                          <DownloadFileIcon />
                        </a>
                      </FileSection>
                    ))}
                  </>
                ) : null}

                <div className="mt-3 mb-1 text-xs font-semibold text-[var(--color-text-muted)]">
                  All files ZIP Archive
                </div>

                <FileSection key={`${name}_reporter_unlock_files.zip`} className="mt-2">
                  <SummaryIcon className="icon" />
                  <div className="file-description">
                    <div>{`${name}_reporter_unlock_files.zip`}</div>
                    {/* {sourceFile.fileSize ? <div>File size {formatFileSize(sourceFile.fileSize)}</div> : null} */}
                  </div>
                  <FileDownloadButton
                    label="Download all files ZIP archive"
                    showLabel={false}
                    onDownload={() =>
                      downloadViaBrowser(
                        `${window.location.origin}/api/jobs/${id}/partial-unlocked-files/download-zip`,
                        `${name}_reporter_unlock_files.zip`,
                      )
                    }
                    onError={handleDownloadError}
                  />
                </FileSection>
              </>
            )}
          </div>
        ) : (
          <UnlockChoiceOption
            className="mt-2"
            onClick={() => {
              if (!partialChargeMutation.isPending) {
                partialChargeMutation.mutate();
              }
            }}
          >
            <div>
              <div className="title">Reporter unlock</div>
              <div className="description">Get summary and audio access</div>
              <div className="price">{formatCentsPrice(partialUnlockRate)}</div>
            </div>
            <ChoiceOptionIcon />
          </UnlockChoiceOption>
        )}

        <div className="buttonWrapper self-end">
          <OutlinedButton onClick={closeModal}>Cancel</OutlinedButton>
        </div>
      </Content>
    </ModalWrapper>
  );
}
